import React, { useEffect, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import type { DeleteReflectorCode } from "./request/request";

/**
 * Copy por código de rechazo.
 *
 * Mismo criterio que la liberación (CTX-351): el backend decide y devuelve el
 * motivo, acá sólo se traduce a algo accionable. En un mapa y no en una cadena
 * de condicionales, para que agregar un caso sea una entrada.
 */
const REFUSAL_COPY: Record<DeleteReflectorCode, string> = {
  REFLECTOR_IN_CLUSTER:
    "This reflector belongs to a cluster. Break the cluster up first, then delete it.",
  REFLECTOR_NOT_FOUND: "This reflector no longer exists.",
  REFLECTOR_DELETE_FAILED: "The reflector could not be deleted. Try again.",
};

export const deleteRefusalMessage = (code?: DeleteReflectorCode): string =>
  (code && REFUSAL_COPY[code]) || REFUSAL_COPY.REFLECTOR_DELETE_FAILED;

/**
 * Si lo tecleado confirma el borrado de este reflector.
 *
 * Se compara sin espacios en los bordes y sin distinguir mayúsculas **a
 * propósito**: el nombre se copia de la pantalla que el usuario tiene delante,
 * así que exigir una coincidencia byte a byte agrega fricción sin agregar
 * seguridad. Y hay nombres reales con espacio final (`"TEST "`), que de otro
 * modo serían imposibles de confirmar a mano.
 */
export const confirmsDeletion = (typed: string, reflectorName: string): boolean =>
  typed.trim().toLowerCase() === reflectorName.trim().toLowerCase() &&
  reflectorName.trim() !== "";

export interface DeleteReflectorModalProps {
  isOpen: boolean;
  /** Nombre del reflector, para que el usuario confirme sobre cuál actúa. */
  reflectorName: string;
  isDeleting: boolean;
  /** Motivo del rechazo. Presente sólo después de un intento refusado. */
  refusal: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirma el borrado diciendo qué implica, y si el backend lo rechaza explica
 * por qué sin cerrarse — el usuario se entera donde pidió la acción.
 *
 * Se nombran las dos consecuencias que el cliente puede observar: pierde la
 * sesión, y el asiento de licencia vuelve a estar disponible.
 *
 * **La ventana de retención NO se menciona, y es deliberado.** De cara al
 * cliente el reflector se elimina, y punto. Que el backend conserve la fila 90
 * días (CTX-542) y que un claim dentro de ese plazo la reactive (CTX-604) es
 * una obligación de retención nuestra, no una promesa al usuario: decirlo
 * convertiría un requisito legal en una funcionalidad que tendríamos que
 * sostener. Si alguien vuelve a agregar ese texto, esta decisión se toma otra
 * vez, no se hereda por descuido.
 *
 * **El borrado se consuma tecleando el nombre**, no con un clic. De cara al
 * usuario esta acción es definitiva, y ese gesto es el que la industria usa
 * para lo definitivo. El campo muestra el nombre como placeholder porque es
 * una transcripción, no una prueba de memoria.
 */
const DeleteReflectorModal: React.FC<DeleteReflectorModalProps> = ({
  isOpen,
  reflectorName,
  isDeleting,
  refusal,
  onConfirm,
  onClose,
}) => {
  const [typed, setTyped] = useState("");
  // El aviso de que no coincide aparece al salir del campo, no en cada tecla:
  // marcar en rojo mientras alguien todavía escribe es regañarlo por ir a medias.
  const [blurred, setBlurred] = useState(false);

  // Abrir el modal empieza de cero. Sin esto, cancelar y volver a abrir dejaría
  // el nombre ya escrito y el borrado a un solo clic — justo lo que el gesto
  // pretende evitar.
  useEffect(() => {
    if (isOpen) {
      setTyped("");
      setBlurred(false);
    }
  }, [isOpen]);

  const confirmed = confirmsDeletion(typed, reflectorName);
  const mismatch = blurred && typed !== "" && !confirmed;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={refusal ? "Cannot delete reflector" : "Delete reflector"}
      size="md"
    >
      <div>
        {refusal ? (
          // El campo de confirmación se va con la oferta: ya no hay nada que
          // confirmar.
          <p className="text-sm text-semantic-text-primary">{refusal}</p>
        ) : (
          <>
            <p className="mb-3 text-sm text-semantic-text-primary">
              Delete <strong>{reflectorName}</strong>?
            </p>

            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-semantic-text-secondary">
              <li>It disappears from every view and loses its connection.</li>
              <li>Its license seat becomes available again.</li>
            </ul>

            <GenericInput
              label={
                <span>
                  Type <strong>{reflectorName}</strong> to confirm
                </span>
              }
              placeholder={reflectorName}
              value={typed}
              onChange={setTyped}
              onBlur={() => setBlurred(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && confirmed && !isDeleting) {
                  onConfirm();
                }
              }}
              hasError={mismatch}
              helperText={
                mismatch ? "That doesn't match. Check for a missing character." : undefined
              }
              disabled={isDeleting}
              fullWidth
            />
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <GenericButton
            variant={refusal ? "brand" : "secondary"}
            label={refusal ? "Close" : "Cancel"}
            onClick={onClose}
            disabled={isDeleting}
          />
          {!refusal && (
            // `danger` y no `brand`: el gesto tiene que verse distinto del que
            // confirma cualquier otra cosa, y no puede ser el que el ojo elige
            // por defecto.
            <GenericButton
              variant="danger"
              label={isDeleting ? "Deleting…" : "Delete reflector"}
              onClick={onConfirm}
              disabled={isDeleting || !confirmed}
            />
          )}
        </div>
      </div>
    </GenericModal>
  );
};

export default DeleteReflectorModal;
